/**
 * Translate with AI: choose languages, see the exact price, confirm, translate.
 *
 * Nothing here runs on its own. Opening the dialog lists the languages on offer and their prices (no model call);
 * the model runs only after "Translate for $X.XX", against a single-use quote for exactly the ticked languages,
 * and the charge is never more than that quote. Each language is priced separately; the minimum charge applies
 * to the total. A language with nothing to translate is shown as up to date and cannot be ticked.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import type { CanonicalDocument } from '../../cdm/types'
import { translationsApiService, type AiDraftResult, type AiLocaleCandidate, type AiQuote } from '../../services/translationsApiService'
import { ApiError } from '../../services/BaseApiService'
import { isPlanUpgradeError } from '../esign/esignShared'
import { formatCents } from '../../utils/billingStanding'

interface Props {
  getDocument: () => CanonicalDocument | null
  templateId?: string
  /** The panel's selected locale, ticked to start with when it has work. */
  preselect: string | null
  onCancel: () => void
  onBusy: (busy: boolean) => void
  onDone: (result: AiDraftResult) => void
  onError: (message: string, upgrade: boolean) => void
}

function describe(c: AiLocaleCandidate): string {
  if (c.upToDate) return 'up to date'
  if (!c.existing) return `new — ${c.strings} string(s)`
  return `existing — ${c.missing} missing` + (c.drafts ? `, ${c.drafts} draft(s) to redo` : '')
}

export default function AiTranslateDialog({ getDocument, templateId, preselect, onCancel, onBusy, onDone, onError }: Props) {
  const [candidates, setCandidates] = useState<AiLocaleCandidate[] | null>(null)
  const [minimumCents, setMinimumCents] = useState(50)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [quote, setQuote] = useState<AiQuote | null>(null)
  const [working, setWorking] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const fail = (err: unknown, fallback: string) =>
    onError(isPlanUpgradeError(err) ? 'Translate with AI is available on the Ultimate plan.' : err instanceof ApiError ? err.message : fallback, isPlanUpgradeError(err))

  useEffect(() => {
    const doc = getDocument()
    if (!doc) { onCancel(); return }
    let live = true
    translationsApiService.aiLanguages(doc, templateId)
      .then(r => {
        if (!live) return
        setCandidates(r.candidates)
        setMinimumCents(r.minimumCents)
        const start = r.candidates.find(c => c.locale === preselect && !c.upToDate)
        setChecked(new Set(start ? [start.locale] : []))
      })
      .catch(err => { if (live) fail(err, 'Could not list the languages.') })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const picked = useMemo(() => (candidates ?? []).filter(c => checked.has(c.locale) && !c.upToDate), [candidates, checked])
  const subtotal = picked.reduce((n, c) => n + c.amountCents, 0)
  const total = subtotal > 0 ? Math.max(minimumCents, subtotal) : 0
  const strings = picked.reduce((n, c) => n + c.strings, 0)
  const repeated = picked.reduce((n, c) => n + c.repeatedStrings, 0)
  // A quote is for an exact selection; changing it means a new quote.
  const quoted = quote && quote.lines.length === picked.length && quote.lines.every(l => checked.has(l.locale)) ? quote : null
  const price = quoted ? quoted.amountCents : total

  function toggle(locale: string) {
    setChecked(prev => {
      const next = new Set(prev)
      if (next.has(locale)) next.delete(locale)
      else next.add(locale)
      return next
    })
    setNote(null)
  }

  async function confirm() {
    const doc = getDocument()
    if (!doc || picked.length === 0) return
    setWorking(true)
    try {
      let q = quoted
      if (!q) {
        q = await translationsApiService.aiQuote(doc, picked.map(c => c.locale), templateId)
        setQuote(q)
        setCandidates(q.candidates)
        if (q.amountCents !== total) {
          // The document changed since the list was priced: show the real price and ask again.
          setNote(`The price is now ${formatCents(q.amountCents)}. Confirm again to translate.`)
          return
        }
      }
      onBusy(true)
      const result = await translationsApiService.aiDraft(doc, q.quoteId, q.lines.map(l => l.locale))
      onDone(result)
    } catch (err) {
      fail(err, 'Translate with AI failed. Nothing was charged.')
    } finally {
      setWorking(false)
      onBusy(false)
    }
  }

  const button: CSSProperties = { padding: '4px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, color: colors.textSecondary, fontSize: fontSize.xs, cursor: 'pointer' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div role="dialog" aria-modal="true" aria-label="Translate with AI"
        style={{ background: colors.surface, borderRadius: radius.md, padding: spacing[3], width: 'min(440px, calc(100vw - 32px))', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: spacing.sm, fontSize: fontSize.sm }}>
        <div style={{ fontWeight: 600, color: colors.text }}>Translate with AI</div>
        <div style={{ color: colors.textSecondary, fontSize: fontSize.xs }}>
          Choose the languages. Missing strings and drafts are translated; accepted translations are never changed. Results are drafts for you to review.
        </div>
        {candidates === null ? (
          <div style={{ color: colors.textMuted }}>Loading languages…</div>
        ) : (
          <ul aria-label="Languages" style={{ listStyle: 'none', margin: 0, padding: 0, overflow: 'auto', flex: 1, minHeight: 0, border: `1px solid ${colors.border}`, borderRadius: radius.sm }}>
            {candidates.map(c => (
              <li key={c.locale} data-testid="ai-language" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderBottom: `1px solid ${colors.border}`, color: c.upToDate ? colors.textMuted : colors.text }}>
                <input type="checkbox" aria-label={`${c.label} (${c.locale})`} checked={!c.upToDate && checked.has(c.locale)} disabled={c.upToDate || working} onChange={() => toggle(c.locale)} />
                <span style={{ flex: 1 }}>{c.label} <span style={{ color: colors.textMuted }}>{c.locale}</span>
                  <span style={{ display: 'block', fontSize: 11, color: colors.textMuted }}>{describe(c)}</span>
                </span>
                <span style={{ fontSize: fontSize.xs }}>{c.upToDate ? '$0.00' : formatCents(c.amountCents)}</span>
              </li>
            ))}
          </ul>
        )}
        {picked.length > 0 && (
          <div data-testid="ai-breakdown" style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
            {picked.map(c => `${c.locale} ${formatCents(c.amountCents)}`).join(' + ')}
            {subtotal < minimumCents ? ` (minimum charge ${formatCents(minimumCents)})` : ''}
          </div>
        )}
        <div role="status" data-testid="ai-price" style={{ color: colors.text }}>
          {picked.length === 0
            ? 'Choose at least one language.'
            : `This will cost ${formatCents(price)}, added to your next invoice. ${strings} string(s), ${repeated} repeated with context.`}
        </div>
        {note && <div style={{ color: colors.warning, fontSize: fontSize.xs }}>{note}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button type="button" onClick={onCancel} disabled={working} style={button}>Cancel</button>
          <button type="button" onClick={() => void confirm()} disabled={working || picked.length === 0}
            style={{ ...button, background: colors.primary, color: '#fff', borderColor: colors.primary }}>
            {working ? 'Translating…' : `Translate for ${formatCents(price)}`}
          </button>
        </div>
      </div>
    </div>
  )
}
